import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useAuth } from '../../auth/AuthContext';
import { normalizeRole } from '../../permissions';

const CLEANING_TYPES = { Routine: '#10b981', 'Post-Discharge': '#6366f1', 'Terminal Disinfection': '#dc2626', Disinfection: '#f97316', 'Terminal Cleaning': '#8b5cf6' };
const STATUS_COLORS = { Pending: '#f59e0b', 'In Progress': '#3b82f6', Done: '#10b981', Verified: '#059669' };
const STATUS_ORDER = ['Pending', 'In Progress', 'Done', 'Verified'];
const LINEN_STATUS_CYCLE = ['Issued', 'Sent to Laundry', 'Received', 'Condemned'];

function LinenTab() {
  const { user } = useAuth();
  const isManager = normalizeRole(user?.role) === 'manager';
  const canEdit = isManager || normalizeRole(user?.role) === 'housekeeping';
  const { data, loading, error, reload } = useLiveData(() => ipApi.getHousekeepingLinen(), []);
  const [linen, setLinen] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setLinen(data?.linen || []);
  }, [data]);

  const updateRow = (taskId, field, value) => {
    setLinen((rows) => rows.map((r) => (r.taskId === taskId ? { ...r, [field]: Number(value) || 0 } : r)));
  };

  const cycleStatus = async (item) => {
    const idx = LINEN_STATUS_CYCLE.indexOf(item.linenStatus || 'Issued');
    const next = LINEN_STATUS_CYCLE[(idx + 1) % LINEN_STATUS_CYCLE.length];
    const updated = linen.map(l => l.taskId === item.taskId ? { ...l, linenStatus: next } : l);
    setLinen(updated);
    setSaving(true);
    try {
      await ipApi.saveHousekeepingLinen({ linen: updated });
      setMsg(`${item.item} status → ${next}`);
      reload();
    } catch (e) {
      setMsg(e.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const saveLinen = async () => {
    setSaving(true);
    setMsg('');
    try {
      await ipApi.saveHousekeepingLinen({ linen });
      setMsg('Linen inventory saved.');
      reload();
    } catch (e) {
      setMsg(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const lowItems = linen.filter((l) => l.clean < l.parLevel);
  const receivedClean = linen.reduce((s, l) => s + Number(l.clean || 0), 0);
  const linenStatusColor = { Issued: '#f59e0b', 'Sent to Laundry': '#3b82f6', Received: '#10b981', Condemned: '#dc2626' };

  return (
    <div>
      {error && <div className="error mb">{error}</div>}
      {msg && <div className="info-banner mb">{msg}</div>}
      {lowItems.length > 0 && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          ⚠ <strong>{lowItems.length}</strong> linen item(s) below par level: {lowItems.map((l) => l.item).join(', ')}
        </div>
      )}
      {loading && <p className="hint">Loading linen inventory…</p>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>🛏 Linen Inventory</h3>
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={saveLinen}>
                {saving ? 'Saving…' : 'Save counts'}
              </button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Item</th><th>Total</th><th>In Use</th><th>Laundry</th><th>Clean</th><th>Par Level</th><th>Status</th><th>Lifecycle</th></tr></thead>
              <tbody>
                {linen.map((l) => {
                  const liStatus = l.linenStatus || 'Issued';
                  return (
                    <tr key={l.taskId || l.item}>
                      <td style={{ fontWeight: 700 }}>{l.item}</td>
                      <td style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{canEdit ? <input type="number" className="input" style={{ width: 64, padding: '2px 6px' }} value={l.stock} onChange={(e) => updateRow(l.taskId, 'stock', e.target.value)} /> : l.stock}</td>
                      <td style={{ color: '#f59e0b', fontWeight: 700 }}>{canEdit ? <input type="number" className="input" style={{ width: 64, padding: '2px 6px' }} value={l.inUse} onChange={(e) => updateRow(l.taskId, 'inUse', e.target.value)} /> : l.inUse}</td>
                      <td style={{ color: '#3b82f6', fontWeight: 700 }}>{canEdit ? <input type="number" className="input" style={{ width: 64, padding: '2px 6px' }} value={l.inLaundry} onChange={(e) => updateRow(l.taskId, 'inLaundry', e.target.value)} /> : l.inLaundry}</td>
                      <td style={{ color: l.clean < l.parLevel ? '#dc2626' : '#10b981', fontWeight: 900 }}>{canEdit ? <input type="number" className="input" style={{ width: 64, padding: '2px 6px' }} value={l.clean} onChange={(e) => updateRow(l.taskId, 'clean', e.target.value)} /> : l.clean}</td>
                      <td style={{ color: '#94a3b8' }}>{l.parLevel}</td>
                      <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: l.clean < l.parLevel ? '#fef2f2' : '#f0fdf4', color: l.clean < l.parLevel ? '#dc2626' : '#16a34a' }}>{l.clean < l.parLevel ? 'Low' : 'OK'}</span></td>
                      <td>
                        {canEdit ? (
                          <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10, background: (linenStatusColor[liStatus] || '#94a3b8') + '18', color: linenStatusColor[liStatus] || '#94a3b8', border: `1px solid ${(linenStatusColor[liStatus] || '#94a3b8')}40` }} onClick={() => cycleStatus(l)}>
                            {liStatus} →
                          </button>
                        ) : <span style={{ fontSize: 10, color: linenStatusColor[liStatus] || '#94a3b8' }}>{liStatus}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>🧺 Today&apos;s Laundry Summary</h3>
          <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0d6e6a, #1e40af)', borderRadius: 14, color: '#fff', marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              {[{ label: 'Sent to Laundry', val: linen.reduce((s, l) => s + Number(l.inLaundry || 0), 0), icon: '📤' }, { label: 'Clean Stock', val: receivedClean, icon: '✅' }, { label: 'Condemned', val: linen.reduce((s, l) => s + Number(l.condemned || 0), 0), icon: '🗑' }].map((s) => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24 }}>{s.icon}</div>
                  <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 28, fontWeight: 900 }}>{s.val}</div>
                  <div style={{ fontSize: 10, opacity: 0.8, fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="hint">Use lifecycle button to advance linen through: Issued → Sent to Laundry → Received → Condemned.</p>
        </div>
      </div>
    </div>
  );
}

function TaskCard({ room, canWork, isManager, onAdvance }) {
  const nextAction = {
    Pending: { label: '▶ Start', next: 'In Progress' },
    'In Progress': { label: '✓ Done', next: 'Done' },
    Done: isManager ? { label: '✓✓ Verify', next: 'Verified' } : null,
  }[room.status];

  return (
    <div className="card" style={{ borderLeft: `4px solid ${STATUS_COLORS[room.status] || '#94a3b8'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 16 }}>{room.roomNo}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{room.ward}</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: `${(CLEANING_TYPES[room.type] || '#94a3b8')}18`, color: CLEANING_TYPES[room.type] || '#94a3b8', border: `1px solid ${(CLEANING_TYPES[room.type] || '#94a3b8')}40`, height: 'fit-content' }}>{room.type}</span>
      </div>
      <div style={{ fontSize: 12, marginBottom: 4 }}>👤 <strong>{room.assignedTo || 'Unassigned'}</strong></div>
      {room.requestedBy && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Requested by {room.requestedBy}</div>}
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>🕐 Scheduled: {room.scheduledAt} · Last: {room.lastCleaned}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: `${STATUS_COLORS[room.status]}18`, color: STATUS_COLORS[room.status], border: `1px solid ${STATUS_COLORS[room.status]}40` }}>{room.status}</span>
        {canWork && nextAction && (
          <button type="button" className={`btn btn-sm ${room.status === 'Done' ? 'btn-secondary' : 'btn-primary'}`} style={{ fontSize: 10 }} onClick={() => onAdvance(room.taskId, nextAction.next)}>
            {nextAction.label}
          </button>
        )}
      </div>
    </div>
  );
}

function CleaningBoardTab({ tasks, canWork, isManager, onAdvance, loading }) {
  const counts = { Pending: 0, 'In Progress': 0, Done: 0, Verified: 0 };
  tasks.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
        {STATUS_ORDER.map((status) => (
          <div key={status} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${STATUS_COLORS[status]}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: STATUS_COLORS[status] }}>{counts[status]}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{status}</div>
          </div>
        ))}
      </div>
      {loading && <p className="hint">Loading tasks…</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, alignItems: 'start' }}>
        {STATUS_ORDER.map((status) => (
          <div key={status}>
            <div style={{ fontSize: 11, fontWeight: 800, color: STATUS_COLORS[status], marginBottom: 8, textTransform: 'uppercase' }}>{status}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tasks.filter((t) => t.status === status).map((room) => (
                <TaskCard key={room.taskId} room={room} canWork={canWork} isManager={isManager} onAdvance={onAdvance} />
              ))}
              {tasks.filter((t) => t.status === status).length === 0 && (
                <p className="hint" style={{ fontSize: 11 }}>No {status.toLowerCase()} tasks</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HousekeepingPage() {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const isHousekeeping = role === 'housekeeping';
  const isManager = role === 'manager';
  const canRequest = ['manager', 'nurse', 'ipd', 'reception'].includes(role);
  const canWork = ['manager', 'nurse', 'housekeeping'].includes(role);

  const { data, loading, error, reload } = useLiveData(() => ipApi.getHousekeepingTasks(100), []);
  const tasks = data?.tasks || [];

  const [tab, setTab] = useState('cleaning');
  const [showForm, setShowForm] = useState(false);
  const [showDischargeForm, setShowDischargeForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    roomNo: '', ward: '', type: 'Routine', assignedTo: '', scheduledAt: new Date().toTimeString().slice(0, 5),
  });

  const [beds, setBeds] = useState([]);
  const [bedsLoaded, setBedsLoaded] = useState(false);
  const [dischargedAdmissions, setDischargedAdmissions] = useState([]);
  const [tcBusy, setTcBusy] = useState(false);

  const loadBeds = async () => {
    if (bedsLoaded) return;
    try {
      const res = await ipApi.getBeds();
      setBeds(res?.beds || []);
      setBedsLoaded(true);
    } catch { setBeds([]); }
  };

  const loadDischarges = async () => {
    try {
      const res = await ipApi.getAdmissions({ includeDischarged: true, limit: 30 });
      const recent = (res?.admissions || []).filter(a => {
        if (a.status !== 'Discharged') return false;
        const d = new Date(a.dischargedAt || a.updatedAt || a.admittedAt);
        return Date.now() - d.getTime() < 48 * 3600 * 1000;
      });
      setDischargedAdmissions(recent);
    } catch { setDischargedAdmissions([]); }
  };

  useEffect(() => {
    if (showForm) loadBeds();
  }, [showForm]);

  useEffect(() => {
    if (showDischargeForm) { loadBeds(); loadDischarges(); }
  }, [showDischargeForm]);

  const advanceTask = async (taskId, status) => {
    try {
      await ipApi.patchHousekeepingTask(taskId, { status });
      reload();
    } catch (err) {
      setMsg(err.message || 'Update failed');
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await ipApi.createHousekeepingTask({
        ...form,
        requestedBy: user?.name || user?.email,
      });
      setShowForm(false);
      setForm({ roomNo: '', ward: '', type: 'Routine', assignedTo: '', scheduledAt: new Date().toTimeString().slice(0, 5) });
      setMsg('Cleaning request submitted.');
      reload();
    } catch (err) {
      setMsg(err.message || 'Could not submit');
    } finally {
      setSaving(false);
    }
  };

  const createTerminalClean = async (admission) => {
    setTcBusy(true);
    try {
      const bed = beds.find(b => b.bedId === admission.bedId || b.bedNo === admission.bed);
      await ipApi.createHousekeepingTask({
        roomNo: admission.bed || admission.bedId || 'Unknown',
        ward: admission.ward || bed?.ward || '',
        type: 'Terminal Cleaning',
        assignedTo: '',
        scheduledAt: new Date().toTimeString().slice(0, 5),
        requestedBy: user?.name || 'System',
        notes: `Terminal clean post-discharge: ${admission.patientName || admission.patientId} (Admission: ${admission.admissionId})`,
      });
      setMsg(`Terminal cleaning task created for bed ${admission.bed || admission.bedId}.`);
      setShowDischargeForm(false);
      reload();
    } catch (err) {
      setMsg(err.message || 'Could not create task');
    } finally { setTcBusy(false); }
  };

  const title = isHousekeeping ? 'Housekeeping — Cleaning Board' : isManager ? 'Housekeeping & Laundry' : 'Request Room Cleaning';
  const subtitle = isHousekeeping
    ? 'Start, complete and verify room cleaning tasks assigned to your team.'
    : isManager
    ? 'Room Cleaning Schedule · Task Board · Linen Inventory · Laundry Tracking · Terminal Disinfection'
    : 'Raise a cleaning or disinfection request for your ward or department.';

  const wardOptions = [...new Set(beds.map(b => b.ward).filter(Boolean))];
  const bedOptions = form.ward ? beds.filter(b => b.ward === form.ward) : beds;

  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      actions={canRequest && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowDischargeForm(s => !s); setShowForm(false); }}>
            🏥 Terminal Clean from Discharge
          </button>
          <button type="button" className="btn btn-primary" onClick={() => { setShowForm((s) => !s); setShowDischargeForm(false); }}>
            {showForm ? 'Close' : '+ Add cleaning task'}
          </button>
        </div>
      )}
    >
      {error && (
        <div className="card mb" style={{ borderColor: '#fca5a5', background: '#fef2f2' }}>
          {error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
        </div>
      )}
      {msg && <p className="hint mb" style={{ color: msg.includes('submitted') || msg.includes('created') ? '#16a34a' : '#dc2626' }}>{msg}</p>}

      {showDischargeForm && (
        <div className="card mb" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <h3 style={{ marginTop: 0, color: '#7c3aed' }}>🏥 Terminal Clean — Recently Discharged</h3>
          <p className="hint" style={{ marginBottom: 10 }}>Select a recently discharged patient to create a Terminal Cleaning task for that bed.</p>
          {dischargedAdmissions.length === 0 && <p className="hint">No recent discharges in the last 48 hours.</p>}
          {dischargedAdmissions.map(a => (
            <div key={a.admissionId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{a.patientName || a.patientId}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Ward: {a.ward} · Bed: {a.bed || a.bedId} · Discharged: {new Date(a.dischargedAt || a.updatedAt).toLocaleString('en-IN')}</div>
              </div>
              <button type="button" className="btn btn-sm btn-primary" style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }} disabled={tcBusy} onClick={() => createTerminalClean(a)}>
                Create Task
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form className="card mb form-grid" onSubmit={addTask}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>New cleaning / disinfection task</h3>
          {bedsLoaded && wardOptions.length > 0 ? (
            <>
              <div className="field"><label>Ward / department</label>
                <select className="input" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value, roomNo: '' })}>
                  <option value="">All wards</option>
                  {wardOptions.map(w => <option key={w}>{w}</option>)}
                </select>
              </div>
              <div className="field"><label>Room / Bed *</label>
                <select className="input" required value={form.roomNo} onChange={(e) => setForm({ ...form, roomNo: e.target.value })}>
                  <option value="">Select bed</option>
                  {bedOptions.map(b => <option key={b.bedId} value={b.bedNo || b.bedId}>{b.bedNo || b.bedId}{b.ward ? ` (${b.ward})` : ''}</option>)}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="field"><label>Room / area *</label><input className="input" required value={form.roomNo} onChange={(e) => setForm({ ...form, roomNo: e.target.value })} placeholder="ICU-01, LAB-01, GW-101" /></div>
              <div className="field"><label>Ward / department</label><input className="input" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} placeholder="ICU, Laboratory, General Ward" /></div>
            </>
          )}
          <div className="field"><label>Task type</label>
            <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {Object.keys(CLEANING_TYPES).map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Scheduled time</label><input type="time" className="input" value={form.scheduledAt} onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} /></div>
          {isManager && (
            <div className="field"><label>Assign to</label><input className="input" value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} placeholder="Housekeeping staff name" /></div>
          )}
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Submit request'}</button>
          </div>
        </form>
      )}

      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'cleaning', label: '🧹 Cleaning Board' },
          ...((isManager || isHousekeeping) ? [{ id: 'linen', label: '🛏 Linen & Laundry' }] : []),
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'cleaning' && <CleaningBoardTab tasks={tasks} canWork={canWork} isManager={isManager} onAdvance={advanceTask} loading={loading} />}
      {tab === 'linen' && (isManager || isHousekeeping) && <LinenTab />}
      {!loading && tab === 'cleaning' && tasks.length === 0 && (
        <p className="hint table-empty">No cleaning tasks yet. Tap &quot;Add cleaning task&quot; to schedule one.</p>
      )}
    </PageShell>
  );
}
